import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Register() {
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "super_admin" });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const navigate = useNavigate();
    const { register } = useAuth();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErr("");
        try {
            await register(form);
            toast.success("Account created — welcome aboard!");
            navigate("/dashboard");
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid place-items-center px-6 py-12 bg-background relative overflow-hidden">
            <div aria-hidden className="absolute -top-40 left-1/2 -translate-x-1/2 w-[480px] h-[480px] rounded-full bg-primary/20 blur-3xl" />
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md glass rounded-3xl p-8 shadow-2xl relative z-10"
            >
                <Link to="/" className="inline-flex items-center gap-2 mb-6">
                    <div className="w-9 h-9 rounded-xl bg-white grid place-items-center shadow-[0_0_20px_rgba(255,87,51,0.15)] overflow-hidden p-1">
                        <img src="/logo.png" alt="ParshHR Logo" className="w-full h-full object-contain" />
                    </div>
                    <span className="font-heading font-bold text-xl">ParshHR</span>
                </Link>
                <h1 className="font-heading text-3xl font-bold tracking-tight">Create your account</h1>
                <p className="text-muted-foreground text-sm mt-1">Start running your team in minutes.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <Label htmlFor="name">Full name</Label>
                        <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="register-name" />
                    </div>
                    <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="register-email" />
                    </div>
                    <div>
                        <Label htmlFor="password">Password</Label>
                        <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="register-password" />
                    </div>
                    <div>
                        <Label htmlFor="role">Role</Label>
                        <select id="role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm" data-testid="register-role">
                            <option value="super_admin">Admin (Business Owner)</option>
                            <option value="employee">Employee</option>
                            <option value="intern">Intern</option>
                            <option value="manager">Manager</option>
                            <option value="hr">HR</option>
                        </select>
                    </div>

                    {err && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="register-error">{err}</div>}

                    <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="register-submit">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
                    </Button>

                    <div className="text-center text-sm text-muted-foreground">
                        Already have an account?{" "}
                        <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">Sign in</Link>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}
